import maplibregl from 'maplibre-gl';
import type { LonLat, Status } from '../api/client';

export interface KartenPunkt {
  id: string;
  position: LonLat;
  status: Status | 'neutral';
  /** Kurztext im Kreis, z. B. „S1“ oder „3“ */
  label: string;
  /** Teil der Vorgabe-Ebene (angeordnet) – doppelt umrandet, nicht verschiebbar */
  vorgabe?: boolean;
  /** nur Hinweis (klein, grau) */
  klein?: boolean;
}

export interface ZugLage {
  linie: LonLat[];
  spitze: LonLat;
  ende: LonLat;
}

// Statusfarben aus den Skizzen; unterscheiden sich auch in der Helligkeit.
export const FARBE: Record<Status | 'neutral', string> = {
  rot: '#B3261E',
  gelb: '#C27410',
  gruen: '#1F6B45',
  polizei: '#2F3A4A',
  grau: '#8A929C',
  neutral: '#1B1F24',
};

const Q = 'punkte';
const QZ = 'zug';
const QZP = 'zug-punkte';
const QR = 'route-anzeige';

/**
 * Stellen als Kreise mit Kurztext; Vorgabe-Stellen mit zusätzlichem Ring. Dazu die Lage des Zuges.
 */
export class PunkteLayer {
  private popup: maplibregl.Popup | null = null;

  constructor(
    private readonly map: maplibregl.Map,
    private readonly onAuswahl: (id: string) => void,
    private readonly popupInhalt?: (id: string) => HTMLElement | null,
  ) {
    map.addSource(Q, { type: 'geojson', data: leer() });
    map.addSource(QZ, { type: 'geojson', data: leer() });
    map.addSource(QZP, { type: 'geojson', data: leer() });
    map.addSource(QR, { type: 'geojson', data: leer() });

    map.addLayer({
      id: 'route-anzeige',
      type: 'line',
      source: QR,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#1D4F91', 'line-width': 5 },
    });

    map.addLayer({
      id: 'zug-linie',
      type: 'line',
      source: QZ,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#F5B400', 'line-width': 12, 'line-opacity': 0.75 },
    });
    map.addLayer({
      id: 'punkte-vorgabe',
      type: 'circle',
      source: Q,
      filter: ['==', ['get', 'vorgabe'], true],
      paint: {
        'circle-radius': ['case', ['get', 'ausgewaehlt'], 21, 17],
        'circle-color': 'rgba(0,0,0,0)',
        'circle-stroke-color': '#1B1F24',
        'circle-stroke-width': 2,
      },
    });
    map.addLayer({
      id: 'punkte-kreis',
      type: 'circle',
      source: Q,
      paint: {
        'circle-radius': ['case', ['get', 'ausgewaehlt'], 15, ['get', 'klein'], 5, 12],
        'circle-color': ['get', 'farbe'],
        'circle-stroke-color': ['case', ['get', 'ausgewaehlt'], '#F5B400', '#FFFFFF'],
        'circle-stroke-width': ['case', ['get', 'ausgewaehlt'], 4, 2],
      },
    });
    map.addLayer({
      id: 'punkte-text',
      type: 'symbol',
      source: Q,
      filter: ['!', ['get', 'klein']],
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Noto Sans Bold'],
        'text-size': 11,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#FFFFFF' },
    });
    map.addLayer({
      id: 'zug-punkte',
      type: 'circle',
      source: QZP,
      paint: {
        'circle-radius': 7,
        'circle-color': '#1B1F24',
        'circle-stroke-color': '#F5B400',
        'circle-stroke-width': 3,
      },
    });
    map.addLayer({
      id: 'zug-text',
      type: 'symbol',
      source: QZP,
      layout: {
        'text-field': ['get', 'text'],
        'text-font': ['Noto Sans Bold'],
        'text-size': 12,
        'text-offset': [0, -1.6],
        'text-allow-overlap': true,
      },
      paint: { 'text-color': '#1B1F24', 'text-halo-color': '#FFFFFF', 'text-halo-width': 2 },
    });

    map.on('click', 'punkte-kreis', (e) => {
      const id = e.features?.[0]?.properties?.id as unknown;
      if (typeof id === 'string') this.onAuswahl(id);
    });
    map.on('mouseenter', 'punkte-kreis', () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', 'punkte-kreis', () => (map.getCanvas().style.cursor = ''));
  }

  setzen(punkte: KartenPunkt[], ausgewaehltId: string | null, folgen = true): void {
    const q = this.map.getSource(Q);
    if (q instanceof maplibregl.GeoJSONSource) {
      q.setData({
        type: 'FeatureCollection',
        features: punkte.map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: p.position },
          properties: {
            id: p.id,
            label: p.label,
            farbe: FARBE[p.status],
            vorgabe: p.vorgabe === true,
            klein: p.klein === true,
            ausgewaehlt: p.id === ausgewaehltId,
          },
        })),
      });
    }
    for (const l of [
      'route-anzeige',
      'zug-linie',
      'punkte-vorgabe',
      'punkte-kreis',
      'punkte-text',
      'zug-punkte',
      'zug-text',
    ]) {
      this.map.moveLayer(l);
    }
    this.popup?.remove();
    this.popup = null;
    const p = punkte.find((x) => x.id === ausgewaehltId);
    const inhalt = p && this.popupInhalt?.(p.id);
    if (p && inhalt) {
      this.popup = new maplibregl.Popup({ closeButton: true, offset: 18, maxWidth: '340px' })
        .setLngLat(p.position)
        .setDOMContent(inhalt)
        .addTo(this.map);
      if (folgen) this.map.easeTo({ center: p.position, duration: 400 });
    }
  }

  /** Route nur anzeigen (nicht bearbeitbar). */
  route(coords: LonLat[] | null): void {
    const q = this.map.getSource(QR);
    if (q instanceof maplibregl.GeoJSONSource) {
      q.setData(
        coords && coords.length >= 2
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: { type: 'LineString', coordinates: coords },
                  properties: {},
                },
              ],
            }
          : leer(),
      );
    }
  }

  zug(lage: ZugLage | null): void {
    const z = this.map.getSource(QZ);
    const zp = this.map.getSource(QZP);
    if (z instanceof maplibregl.GeoJSONSource) {
      z.setData(
        lage
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: { type: 'LineString', coordinates: lage.linie },
                  properties: {},
                },
              ],
            }
          : leer(),
      );
    }
    if (zp instanceof maplibregl.GeoJSONSource) {
      zp.setData(
        lage
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: lage.spitze },
                  properties: { text: 'Zugspitze' },
                },
                {
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: lage.ende },
                  properties: { text: 'Zugende' },
                },
              ],
            }
          : leer(),
      );
    }
  }
}

function leer(): GeoJSON.FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}
